import { Injectable } from '@angular/core';
import { Observable, of, delay, tap } from 'rxjs';
import { CareerSetup, CareerSetupParams } from '../../interfaces/career-setup.interface';
import { PageResponse } from '../../interfaces/page-response.interface';
import { CareerSetupService } from './career-setup.service';
import { CommonSearchParam } from '../../interfaces/common-search-param.interface';

@Injectable({
  providedIn: 'root'
})
export class CareerSetupMockService extends CareerSetupService {
  private mockData: CareerSetup[] = [
    // Industries
    { uniqueId: 'ind-it', category: 'INDUSTRY', name: 'Information Technology', active: true },
    { uniqueId: 'ind-fin', category: 'INDUSTRY', name: 'Finance', active: true },
    { uniqueId: 'ind-hc', category: 'INDUSTRY', name: 'Healthcare', active: true },
    { uniqueId: 'ind-edu', category: 'INDUSTRY', name: 'Education', active: true },
    
    // Roles
    { uniqueId: 'role-fe', category: 'ROLE', name: 'Frontend Developer', active: true, parentUniqueId: 'ind-it' },
    { uniqueId: 'role-be', category: 'ROLE', name: 'Backend Developer', active: true, parentUniqueId: 'ind-it' },
    { uniqueId: 'role-fs', category: 'ROLE', name: 'Fullstack Developer', active: true, parentUniqueId: 'ind-it' },
    { uniqueId: 'role-devops', category: 'ROLE', name: 'DevOps Engineer', active: true, parentUniqueId: 'ind-it' },
    { uniqueId: 'role-fa', category: 'ROLE', name: 'Financial Analyst', active: true, parentUniqueId: 'ind-fin' },
    { uniqueId: 'role-inv-bank', category: 'ROLE', name: 'Investment Banker', active: true, parentUniqueId: 'ind-fin' },
    
    // Skills (nested structure for hierarchy)
    { uniqueId: 'skill-fe', category: 'SKILL', name: 'Frontend Skills', skills: ['Angular', 'React', 'Vue', 'TypeScript', 'SCSS', 'HTML5', 'CSS3', 'JavaScript'], active: true, parentUniqueId: 'role-fe' },
    { uniqueId: 'skill-be', category: 'SKILL', name: 'Backend Skills', skills: ['Node.js', 'Express', 'Python', 'Django', 'Java', 'Spring Boot', 'PostgreSQL', 'MongoDB'], active: true, parentUniqueId: 'role-be' },
    { uniqueId: 'skill-fs', category: 'SKILL', name: 'Fullstack Skills', skills: ['Angular', 'Node.js', 'PostgreSQL', 'TypeScript', 'Docker', 'AWS'], active: true, parentUniqueId: 'role-fs' },
    { uniqueId: 'skill-devops', category: 'SKILL', name: 'DevOps Skills', skills: ['Docker', 'Kubernetes', 'AWS', 'Terraform', 'Jenkins', 'CI/CD'], active: true, parentUniqueId: 'role-devops' }
  ];

  getCareerSetup(params: CareerSetupParams): Observable<CareerSetup[]> {
    let filtered = this.mockData.filter(item => item.category === params.category);
    if (params.parentUniqueId) {
      filtered = filtered.filter(item => item.parentUniqueId === params.parentUniqueId);
    }
    return of(filtered).pipe(delay(500));
  }

  override findAll(params: CommonSearchParam): Observable<PageResponse<CareerSetup>> {
    let filtered = [...this.mockData];
    if (params.searchTerm) {
      const term = params.searchTerm.toLowerCase();
      filtered = filtered.filter(i => i.name.toLowerCase().includes(term));
    }
    
    return of({
      content: filtered,
      page: { number: params.page || 0, size: params.size || 10, totalElements: filtered.length, totalPages: 1 }
    }).pipe(delay(500));
  }

  override find(id: string): Observable<CareerSetup> {
    const item = this.mockData.find(i => i.uniqueId === id);
    if (!item) {
      return of(null as any).pipe(delay(500), tap(() => { throw new Error('Not found'); }));
    }
    return of(item).pipe(delay(500));
  }

  override create(data: Partial<CareerSetup>): Observable<CareerSetup> {
    const newItem: CareerSetup = {
      ...data as CareerSetup,
      uniqueId: `mock-${Date.now()}`,
      active: data.active !== false
    };
    this.mockData.push(newItem);
    return of(newItem).pipe(delay(500));
  }

  override update(id: string, data: Partial<CareerSetup>): Observable<CareerSetup> {
    const index = this.mockData.findIndex(i => i.uniqueId === id);
    if (index === -1) {
       return of(null as any).pipe(delay(500), tap(() => { throw new Error('Not found'); }));
    }
    this.mockData[index] = { ...this.mockData[index], ...data };
    return of(this.mockData[index]).pipe(delay(500));
  }

  override delete(id: string): Observable<void> {
    const index = this.mockData.findIndex(i => i.uniqueId === id);
    if (index !== -1) {
      this.mockData.splice(index, 1);
    }
    return of(undefined).pipe(delay(500));
  }

  override updateStatus(id: string, isActive: boolean, category?: string): Observable<CareerSetup> {
    const index = this.mockData.findIndex(i => i.uniqueId === id);
    if (index === -1) {
       return of(null as any).pipe(delay(500), tap(() => { throw new Error('Not found'); }));
    }
    this.mockData[index] = { ...this.mockData[index], active: isActive, isActive: isActive };
    return of(this.mockData[index]).pipe(delay(500));
  }

  getHierarchy(industryId: string): Observable<any> {
    const industry = this.mockData.find(i => i.uniqueId === industryId) || { uniqueId: industryId, name: 'Information Technology' };
    const roles = this.mockData.filter(i => i.category === 'ROLE' && i.parentUniqueId === industryId);
    
    // Fallback roles if none match (e.g. for dynamic IDs)
    const activeRoles = roles.length > 0 ? roles : [
      { uniqueId: 'role-1', name: 'Frontend Developer' },
      { uniqueId: 'role-2', name: 'Backend Developer' },
      { uniqueId: 'role-3', name: 'DevOps Engineer' }
    ];

    const hierarchy = {
      uniqueId: industry?.uniqueId,
      name: industry?.name,
      roles: activeRoles.map((r: any, idx) => {
        let skills = ['HTML', 'CSS', 'JavaScript', 'Angular', 'React'];
        let stages: any[] = [];

        if (idx === 0) {
          skills = ['HTML', 'CSS', 'JavaScript', 'Angular', 'React'];
          stages = [
            { uniqueId: 's1', name: 'Aptitude Test', type: 'APTITUDE', orderNo: 1, assessments: [{ uniqueId: 'a1', name: 'General Aptitude', questionsCount: 40 }] },
            { uniqueId: 's2', name: 'UI/UX Screening', type: 'TECHNICAL', orderNo: 2, assessments: [{ uniqueId: 'a2', name: 'Figma to Code', questionsCount: 5 }] },
            { uniqueId: 's3', name: 'Pair Programming', type: 'CODING', orderNo: 3, assessments: [] },
            { uniqueId: 's4', name: 'HR Round', type: 'HR', orderNo: 4, assessments: [] }
          ];
        } else if (idx === 1) {
          skills = ['Node.js', 'Python', 'Java', 'SQL', 'MongoDB'];
          stages = [
            { uniqueId: 's1', name: 'DSA Round', type: 'TECHNICAL', orderNo: 1, assessments: [{ uniqueId: 'a1', name: 'Data Structures & Algorithms', questionsCount: 30 }] },
            { uniqueId: 's2', name: 'System Design', type: 'TECHNICAL', orderNo: 2, assessments: [{ uniqueId: 'a3', name: 'High Level Design', questionsCount: 10 }] },
            { uniqueId: 's4', name: 'HR Round', type: 'HR', orderNo: 4, assessments: [] }
          ];
        } else {
          skills = ['Docker', 'Kubernetes', 'AWS', 'Terraform', 'CI/CD'];
          stages = [
            { uniqueId: 's1', name: 'Infrastructure Test', type: 'TECHNICAL', orderNo: 1, assessments: [{ uniqueId: 'a1', name: 'AWS Configuration', questionsCount: 20 }] },
            { uniqueId: 's2', name: 'Live Troubleshooting', type: 'CODING', orderNo: 2, assessments: [] }
          ];
        }

        return {
          uniqueId: r.uniqueId,
          name: r.name,
          // In career-details getFlatSkills expects: { skills: ['A', 'B'] } from array
          skills: [{ skills: skills }],
          stages: stages
        };
      })
    };
    return of(hierarchy).pipe(delay(500));
  }

  getConfigured(industryId: string): Observable<any> {
    // For mock, we return the same structure as hierarchy
    return this.getHierarchy(industryId);
  }
}
